import { useNode } from "@craftjs/core";
import React from "react";
import { changeTransition, isUpdatedFromDefault } from "../../utils";

import {
  SelectInput,
  Tabs,
  Spacer,
  RichTextFormatting,
  Panel,
  UnitInput,
  TabsV2,
  RangeNumberInput,
  ColorInput,
  PaddingInput,
  MarginInput,
  BorderInput,
} from "../../input";
import { supportLibrary } from "../../block-supports";

export const FAQSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    listStyle = "ul",
    titleWrapper = "h3",
    questionTypoSize,
    questionTypoSizeTablet,
    questionTypoSizeMobile,
    questionTypoLineHeight,
    questionTypoLetterSpacing,
    questionTypoWordSpacing,
    questionTypoTransform,
    questionTypoStyle,
    questionTypoDecoration,
    questionTypoWeight,
    answerTypoSize,
    answerTypoSizeTablet,
    answerTypoSizeMobile,
    answerTypoLineHeight,
    answerTypoLetterSpacing,
    answerTypoWordSpacing,
    answerTypoTransform,
    answerTypoStyle,
    answerTypoDecoration,
    answerTypoWeight,
    questionColor,
    answerColor,
    questionBackgroundColor,
    answerBackgroundColor,
    layoutMargin,
    layoutPadding,
    border,
    hoverQuestionColor,
    hoverAnswerColor,
    hoverQuestionBackgroundColor,
    hoverAnswerBackgroundColor,
    hoverLayoutMargin,
    hoverLayoutPadding,
    hoverBorder,
    transition,
  } = props;

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing="1rem">
          <SelectInput
            resetValue={"ul"}
            value={listStyle}
            onChange={(val) => {
              setProp((props) => {
                props.listStyle = val;
              });
            }}
            options={[
              {
                label: "ul",
                value: "ul",
              },
              {
                label: "ol",
                value: "ol",
              },
            ]}
            label={"List Style"}
          />
          <SelectInput
            resetValue={"h3"}
            value={titleWrapper}
            onChange={(val) => {
              setProp((props) => {
                props.titleWrapper = val;
              });
            }}
            options={[
              {
                label: "Heading 1",
                value: "h1",
              },
              {
                label: "Heading 2",
                value: "h2",
              },
              {
                label: "Heading 3",
                value: "h3",
              },
              {
                label: "Heading 4",
                value: "h4",
              },
              {
                label: "Heading 5",
                value: "h5",
              },
              {
                label: "Heading 6",
                value: "h6",
              },
            ]}
            label={"Title Wrapper"}
          />
        </Spacer>
        <Panel
          title={"Typography - Question"}
          initialOpen={false}
          id={`block-setting-${id}-typography`}
          isUpdated={isUpdatedFromDefault([
            { key: questionTypoSize },
            { key: questionTypoSizeTablet },
            { key: questionTypoSizeMobile },
            { key: questionTypoLineHeight },
            { key: questionTypoLetterSpacing },
            { key: questionTypoWordSpacing },
            { key: questionTypoTransform },
            { key: questionTypoStyle },
            { key: questionTypoWeight },
            { key: questionTypoDecoration },
            { key: questionColor },
          ])}
        >
          <UnitInput
            value={questionTypoSize}
            isResponsive={true}
            valueTablet={questionTypoSizeTablet}
            valueMobile={questionTypoSizeMobile}
            onChange={(val) => {
              setProp((props) => {
                props.questionTypoSize = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.questionTypoSizeTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.questionTypoSizeMobile = val;
              });
            }}
            label={"Size"}
          />
          <UnitInput
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            value={questionTypoLineHeight}
            onChange={(val) => {
              setProp((props) => {
                props.questionTypoLineHeight = val;
              });
            }}
            label={"Line Height"}
          />
          <UnitInput
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            value={questionTypoLetterSpacing}
            onChange={(val) => {
              setProp((props) => {
                props.questionTypoLetterSpacing = val;
              });
            }}
            label={"Letter Spacing"}
          />
          <UnitInput
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            value={questionTypoWordSpacing}
            onChange={(val) => {
              setProp((props) => {
                props.questionTypoWordSpacing = val;
              });
            }}
            label={"Word Spacing"}
          />
          <SelectInput
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            value={questionTypoWeight}
            onChange={(val) => {
              setProp((props) => {
                props.questionTypoWeight = val;
              });
            }}
            options={[
              { label: "100", value: "100" },
              { label: "200", value: "200" },
              { label: "300", value: "300" },
              { label: "400", value: "400" },
              { label: "500", value: "500" },
              { label: "600", value: "600" },
              { label: "700", value: "700" },
              { label: "800", value: "800" },
              { label: "900", value: "900" },
            ]}
            label="Weight"
          />
          <SelectInput
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            value={questionTypoTransform}
            onChange={(val) => {
              setProp((props) => {
                props.questionTypoTransform = val;
              });
            }}
            options={[
              { label: "Uppercase", value: "uppercase" },
              { label: "Lowercase", value: "lowercase" },
              { label: "Capitalize", value: "capitalize" },
              { label: "None", value: "none" },
            ]}
            label="Transform"
          />
          <SelectInput
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            value={questionTypoDecoration}
            onChange={(val) => {
              setProp((props) => {
                props.questionTypoDecoration = val;
              });
            }}
            options={[
              { label: "Underline", value: "underline" },
              { label: "Overline", value: "overline" },
              { label: "None", value: "none" },
            ]}
            label="Decoration"
          />
          <SelectInput
            resetValue={undefined}
            value={questionTypoStyle}
            onChange={(val) => {
              setProp((props) => {
                props.questionTypoStyle = val;
              });
            }}
            options={[
              {
                label: "Normal",
                value: "normal",
              },
              {
                label: "Italic",
                value: "italic",
              },
            ]}
            label="Style"
          />
        </Panel>
        <Panel
          title={"Typography - Answer"}
          initialOpen={false}
          id={`block-setting-${id}-typography`}
          isUpdated={isUpdatedFromDefault([
            { key: answerTypoSize },
            { key: answerTypoSizeTablet },
            { key: answerTypoDecoration },
            { key: answerTypoSizeMobile },
            { key: answerTypoLineHeight },
            { key: answerTypoLetterSpacing },
            { key: answerTypoWordSpacing },
            { key: answerTypoTransform },
            { key: answerTypoStyle },
            { key: answerTypoWeight },
            { key: answerColor },
          ])}
        >
          <UnitInput
            value={answerTypoSize}
            isResponsive={true}
            valueTablet={answerTypoSizeTablet}
            valueMobile={answerTypoSizeMobile}
            onChange={(val) => {
              setProp((props) => {
                props.answerTypoSize = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.answerTypoSizeTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.answerTypoSizeMobile = val;
              });
            }}
            label={"Size"}
          />
          <UnitInput
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            value={answerTypoLineHeight}
            onChange={(val) => {
              setProp((props) => {
                props.answerTypoLineHeight = val;
              });
            }}
            label={"Line Height"}
          />
          <UnitInput
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            value={answerTypoLetterSpacing}
            onChange={(val) => {
              setProp((props) => {
                props.answerTypoLetterSpacing = val;
              });
            }}
            label={"Letter Spacing"}
          />
          <UnitInput
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            value={answerTypoWordSpacing}
            onChange={(val) => {
              setProp((props) => {
                props.answerTypoWordSpacing = val;
              });
            }}
            label={"Word Spacing"}
          />
          <SelectInput
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            value={answerTypoWeight}
            onChange={(val) => {
              setProp((props) => {
                props.answerTypoWeight = val;
              });
            }}
            options={[
              { label: "100", value: "100" },
              { label: "200", value: "200" },
              { label: "300", value: "300" },
              { label: "400", value: "400" },
              { label: "500", value: "500" },
              { label: "600", value: "600" },
              { label: "700", value: "700" },
              { label: "800", value: "800" },
              { label: "900", value: "900" },
            ]}
            label="Weight"
          />
          <SelectInput
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            value={answerTypoTransform}
            onChange={(val) => {
              setProp((props) => {
                props.answerTypoTransform = val;
              });
            }}
            options={[
              { label: "Uppercase", value: "uppercase" },
              { label: "Lowercase", value: "lowercase" },
              { label: "Capitalize", value: "capitalize" },
              { label: "None", value: "none" },
            ]}
            label="Transform"
          />
          <SelectInput
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            value={answerTypoDecoration}
            onChange={(val) => {
              setProp((props) => {
                props.answerTypoDecoration = val;
              });
            }}
            options={[
              { label: "Underline", value: "underline" },
              { label: "Overline", value: "overline" },
              { label: "None", value: "none" },
            ]}
            label="Decoration"
          />
          <SelectInput
            resetValue={undefined}
            value={answerTypoStyle}
            onChange={(val) => {
              setProp((props) => {
                props.answerTypoStyle = val;
              });
            }}
            options={[
              {
                label: "Normal",
                value: "normal",
              },
              {
                label: "Italic",
                value: "italic",
              },
            ]}
            label="Style"
          />
        </Panel>
        <RichTextFormatting />
      </div>
    );
  }

  function StyleSettings() {
    const questionColorTabs = [
      {
        label: "Normal",
        key: "normal",
        children: handleColorSettings({
          questionColorPropKey: "questionColor",
          questionColorProp: questionColor,
          questionBackgroundColorPropKey: "questionBackgroundColor",
          questionBackgroundColorProp: questionBackgroundColor,
        }),
      },
      {
        label: "Hover",
        key: "hover",
        children: handleColorSettings({
          questionColorPropKey: "hoverQuestionColor",
          questionColorProp: hoverQuestionColor,
          questionBackgroundColorPropKey: "hoverQuestionBackgroundColor",
          questionBackgroundColorProp: hoverQuestionBackgroundColor,
        }),
      },
    ];

    function handleColorSettings({
      questionColorPropKey,
      questionColorProp,
      questionBackgroundColorPropKey,
      questionBackgroundColorProp,
    }) {
      return (
        <>
          {" "}
          <ColorInput
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            value={questionColorProp}
            onChange={(val) => {
              setProp((props) => {
                props[questionColorPropKey] = val;
              });
            }}
            label={"Text"}
          />
          <ColorInput
            resetValue={undefined}
            value={questionBackgroundColorProp}
            onChange={(val) => {
              setProp((props) => {
                props[questionBackgroundColorPropKey] = val;
              });
            }}
            label={"Background"}
          />
          {(questionBackgroundColorPropKey === "hoverQuestionBackgroundColor" ||
            questionColorPropKey === "hoverQuestionColor") && (
            <RangeNumberInput
              min={0}
              max={10}
              step={0.05}
              value={
                transition?.types?.[("color", "background-color")]?.duration
              }
              onChange={(val) => {
                changeTransition(
                  ["color", "background-color"],
                  { duration: val },
                  transition,
                  setProp,
                );
              }}
              label={"Transition Time"}
            />
          )}
        </>
      );
    }

    const answerColorTabs = [
      {
        label: "Normal",
        key: "normal",
        children: handleBackgroundColorSettings({
          answerColorPropKey: "answerColor",
          answerColorProp: answerColor,
          answerBackgroundColorPropKey: "answerBackgroundColor",
          answerBackgroundColorProp: answerBackgroundColor,
        }),
      },
      {
        label: "Hover",
        key: "hover",
        children: handleBackgroundColorSettings({
          answerColorPropKey: "hoverAnswerColor",
          answerColorProp: hoverAnswerColor,
          answerBackgroundColorPropKey: "hoverAnswerBackgroundColor",
          answerBackgroundColorProp: hoverAnswerBackgroundColor,
        }),
      },
    ];

    function handleBackgroundColorSettings({
      answerColorPropKey,
      answerColorProp,
      answerBackgroundColorPropKey,
      answerBackgroundColorProp,
    }) {
      return (
        <>
          {" "}
          <ColorInput
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            value={answerColorProp}
            onChange={(val) => {
              setProp((props) => {
                props[answerColorPropKey] = val;
              });
            }}
            label={"Text"}
          />
          <ColorInput
            resetValue={undefined}
            value={answerBackgroundColorProp}
            onChange={(val) => {
              setProp((props) => {
                props[answerBackgroundColorPropKey] = val;
              });
            }}
            label={"Background"}
          />
          {(answerColorPropKey === "hoverAnswerColor" ||
            answerBackgroundColorPropKey === "hoverAnswerBackgroundColor") && (
            <RangeNumberInput
              min={0}
              max={10}
              step={0.05}
              value={
                transition?.types?.[("color", "background-color")]?.duration
              }
              onChange={(val) => {
                changeTransition(
                  ["color", "background-color"],
                  { duration: val },
                  transition,
                  setProp,
                );
              }}
              label={"Transition Time"}
            />
          )}
        </>
      );
    }

    return (
      <>
        <Panel
          id={`block-setting-${id}-colors`}
          title={"Question - Colors"}
          initialOpen={true}
          isUpdated={isUpdatedFromDefault([
            { key: questionBackgroundColor },
            { key: hoverQuestionColor },
            { key: hoverQuestionBackgroundColor },
            { key: questionColor },
          ])}
        >
          <TabsV2 items={questionColorTabs} />
        </Panel>
        <Panel
          id={`block-setting-${id}-colors`}
          title={"Answer - Colors"}
          initialOpen={true}
          isUpdated={isUpdatedFromDefault([
            { key: answerColor },
            { key: answerBackgroundColor },
            { key: hoverAnswerColor },
            { key: hoverAnswerBackgroundColor },
          ])}
        >
          <TabsV2 items={answerColorTabs} />
        </Panel>
      </>
    );
  }

  function AdvancedSettings() {
    const layoutTabs = [
      {
        label: "Normal",
        key: "normal",
        children: handleLayoutSettings({
          layoutMarginPropKey: "layoutMargin",
          layoutMarginProp: layoutMargin,
          layoutPaddingPropKey: "layoutPadding",
          layoutPaddingProp: layoutPadding,
        }),
      },
      {
        label: "Hover",
        key: "hover",
        children: handleLayoutSettings({
          layoutMarginPropKey: "hoverLayoutMargin",
          layoutMarginProp: hoverLayoutMargin,
          layoutPaddingPropKey: "hoverLayoutPadding",
          layoutPaddingProp: hoverLayoutPadding,
        }),
      },
    ];
    function handleLayoutSettings({
      layoutMarginPropKey,
      layoutMarginProp,
      layoutPaddingPropKey,
      layoutPaddingProp,
    }) {
      return (
        <>
          <MarginInput
            resetValue={undefined}
            value={layoutMarginProp}
            onChange={(val) => {
              setProp((props) => {
                props[layoutMarginPropKey] = val;
              });
            }}
            label={"Margin"}
          />
          <PaddingInput
            resetValue={undefined}
            value={layoutPaddingProp}
            onChange={(val) => {
              setProp((props) => {
                props[layoutPaddingPropKey] = val;
              });
            }}
            label={"Padding"}
          />
          {(layoutMarginPropKey === "hoverLayoutMargin" ||
            layoutPaddingPropKey === "hoverLayoutPadding") && (
            <RangeNumberInput
              min={0}
              max={10}
              step={0.05}
              value={
                transition?.types?.[("border", "margin", "padding")]?.duration
              }
              onChange={(val) => {
                changeTransition(
                  ["border", "margin", "padding"],
                  { duration: val },
                  transition,
                  setProp,
                );
              }}
              label={"Transition Time"}
            />
          )}
        </>
      );
    }

    const borderTabs = [
      {
        label: "Normal",
        key: "normal",
        children: handleBorderSettings({
          borderPropKey: "border",
          borderProp: border,
        }),
      },
      {
        label: "Hover",
        key: "hover",
        children: handleBorderSettings({
          borderPropKey: "hoverBorder",
          borderProp: hoverBorder,
        }),
      },
    ];
    function handleBorderSettings({ borderPropKey, borderProp }) {
      return (
        <>
          <BorderInput
            resetValue={undefined}
            value={borderProp}
            onChange={(val) => {
              setProp((props) => {
                props[borderPropKey] = val;
              });
            }}
            label={"Border"}
          />
          {borderPropKey === "hoverBorder" && (
            <RangeNumberInput
              min={0}
              max={10}
              step={0.05}
              value={transition?.types?.["border"]?.duration}
              onChange={(val) => {
                changeTransition(
                  ["border"],
                  { duration: val },
                  transition,
                  setProp,
                );
              }}
              label={"Transition Time"}
            />
          )}
        </>
      );
    }
    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: layoutMargin },
            { key: layoutPadding },
            { key: border },
            { key: hoverLayoutMargin },
            { key: hoverLayoutPadding },
            { key: hoverBorder },
          ])}
          id={`block-setting-${id}-layout`}
          title={"Layout"}
          initialOpen={true}
        >
          <TabsV2 items={layoutTabs} />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: layoutMargin },
            { key: layoutPadding },
            { key: border },
            { key: hoverLayoutMargin },
            { key: hoverLayoutPadding },
            { key: hoverBorder },
          ])}
          id={`block-setting-${id}-border`}
          title={"Border"}
          initialOpen={false}
        >
          <TabsV2 items={borderTabs} />
        </Panel>
        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: questionTypoSize },
        { key: questionTypoSizeTablet },
        { key: questionTypoSizeMobile },
        { key: questionTypoLineHeight },
        { key: questionTypoLetterSpacing },
        { key: questionTypoWordSpacing },
        { key: questionTypoTransform },
        { key: questionTypoStyle },
        { key: questionTypoWeight },
        { key: questionTypoDecoration },
        { key: questionColor },
        { key: answerTypoSize },
        { key: answerTypoSizeTablet },
        { key: answerTypoDecoration },
        { key: answerTypoSizeMobile },
        { key: answerTypoLineHeight },
        { key: answerTypoLetterSpacing },
        { key: answerTypoWordSpacing },
        { key: answerTypoTransform },
        { key: answerTypoStyle },
        { key: answerTypoWeight },
        { key: answerColor },
      ]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: answerColor },
        { key: answerBackgroundColor },
        { key: hoverAnswerColor },
        { key: hoverAnswerBackgroundColor },
        { key: questionBackgroundColor },
        { key: hoverQuestionColor },
        { key: hoverQuestionBackgroundColor },
        { key: questionColor },
      ]),
    },
    {
      label: "Advance",
      key: "advance",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: layoutMargin },
        { key: layoutPadding },
        { key: border },
        { key: hoverLayoutMargin },
        { key: hoverLayoutPadding },
        { key: hoverBorder },
      ]),
    },
  ];

  return (
    <div>
      <Tabs
        id={`block-settings-tab-${id}`}
        type="block-setting"
        items={tabs.map((item, i) => {
          return {
            label: item.label,
            key: item.key,
            children: item.children,
          };
        })}
      />
    </div>
  );
};
